/**
 * EraGroup.tsx — wraps era-filtered children and damps material opacity per frame.
 *
 * Source: 04-RESEARCH.md Pattern 2 + Pitfalls #1 + #4.
 *
 * What this component does:
 *   1. Reads currentEra from useSceneStore
 *   2. Computes targetOpacity for its `era` prop (1 when active, 0 when not)
 *   3. Every frame via useFrame: traverses its subtree, damps each material's
 *      opacity toward target, toggles depthWrite at the 0.95 threshold (z-fighting
 *      mitigation per Pitfall #1), and flips eraTransitioning when actively damping
 *   4. Hides the group entirely when fully transparent (frustum culling gain)
 *
 * What this component does NOT do:
 *   - Move the camera (ERA-02 invariant — camera pose MUST be unchanged)
 *   - Attach raycast handlers (Plan 04-03 scope)
 *   - Write to useSceneStore.era (Plan 04-04 EraToggle owns writes)
 *   - Apply confidence-tier material treatment (PlaceholderScene does it at mount)
 *
 * Pitfall #4 (rapid re-target):
 *   damp is re-targetable mid-animation — no state machine, no startTime.
 *   Rapid era toggles resolve cleanly because each frame fresh-reads currentEra
 *   and damps toward whatever target that produces.
 */
import { useFrame } from '@react-three/fiber';
import { useRef } from 'react';
import * as THREE from 'three';
import { useSceneStore } from '../store/useSceneStore';
import {
  computeTargetOpacity,
  dampToTarget,
  CROSSFADE_HALF_LIFE_MS,
  type Era,
} from '../era/controller';

const DEPTH_WRITE_THRESHOLD = 0.95;
const SETTLED_EPSILON = 0.001;

interface Props {
  era: Era;
  children: React.ReactNode;
}

export function EraGroup({ era, children }: Props): React.JSX.Element {
  const ref = useRef<THREE.Group>(null);
  const currentEra = useSceneStore((s) => s.era);
  const setTransitioning = useSceneStore((s) => s.setEraTransitioning);

  useFrame((_, delta) => {
    if (!ref.current) return;
    const target = computeTargetOpacity(era, currentEra);
    let maxDelta = 0;
    let maxOpacity = 0;

    ref.current.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh) return;
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const mat of materials) {
        const m = mat as THREE.Material & {
          opacity: number;
          transparent: boolean;
          depthWrite: boolean;
        };
        m.transparent = true;
        const prev = m.opacity;
        m.opacity = dampToTarget(m.opacity, target, CROSSFADE_HALF_LIFE_MS, delta);
        // Pitfall #1: depthWrite OFF when mid-blend so fading side doesn't
        // sort-flip against fully-opaque side. ON near full opacity.
        m.depthWrite = m.opacity > DEPTH_WRITE_THRESHOLD;
        maxDelta = Math.max(maxDelta, Math.abs(m.opacity - prev));
        maxOpacity = Math.max(maxOpacity, m.opacity);
      }
    });

    // Hide the group entirely when fully transparent — skips frustum+raycast work
    ref.current.visible = target > 0 || maxOpacity > 0.01;

    // Write transitioning ONLY on change to avoid Zustand re-render churn
    const active = maxDelta > SETTLED_EPSILON;
    const current = useSceneStore.getState().eraTransitioning;
    if (active !== current) setTransitioning(active);
  });

  return <group ref={ref}>{children}</group>;
}
